import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { UserIdsPicker, UserNameLabel } from './UserIdsPicker'
import { useFeatureFlag } from '../lib/featureFlags'
import { Button, Callout, Card, CardContent, CardHeader, DataTable, DataTableEmptyRow, Divider, IconButton, List, ListItem, Select, TextField } from './ui'
import {
  partnerPositionsApi,
  type PartnerLink,
  type PartnerPositionRow,
  type PartnerPositions,
} from '../api/treasury'

/**
 * F-152 / TASK-0406 (REQ-132-001) — le posizioni dei soci LETTE da conti.equalstudio.it (quota, quota economica, saldo
 * socio/società, già liquidato, da liquidare e la disponibilità distribuibile gestionale). Il CRM non le calcola e non scrive
 * mai su conti.
 *
 * <b>Chi vede cosa.</b> L'Admin vede tutte le righe dei soci e gestisce i collegamenti utente-socio; il socio collegato vede
 * solo la propria riga; chiunque altro non vede nulla (il pannello non compare). Il server impone lo stesso: qui si decide
 * solo cosa disegnare. La lettura dei soci nasce SPENTA lato server (`Integrations:Conti:Partners:Enabled`).
 *
 * Mai IBAN, conti personali, compensazioni o terzi: non arrivano dal server. Componenti M3.
 * Si monta dietro il flag `finanza.soci_da_conti`.
 */

const REASON: Record<string, string> = {
  disabled: "L'integrazione con conti non è attiva.",
  partners_disabled: 'La lettura dei soci da conti non è attiva.',
  not_configured: "L'integrazione con conti non è configurata.",
  timeout: 'Conti non ha risposto in tempo.',
  unreachable: 'Conti non è raggiungibile.',
  unauthorized: 'Conti ha rifiutato le credenziali del CRM.',
  invalid_response: 'Conti ha risposto con dati non leggibili.',
  error: 'Conti ha risposto con un errore.',
}

function eur(n: number | null | undefined): string {
  return n == null ? '—' : '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function pct(n: number): string {
  return (n * 100).toLocaleString('it-IT', { maximumFractionDigits: 2 }) + '%'
}

function PositionTable({ rows }: { rows: PartnerPositionRow[] }) {
  return (
    <DataTable variant="plain" aria-label="Posizioni dei soci">
      <thead>
        <tr>
          <th>Socio</th>
          <th className="md-num">Quota</th>
          <th className="md-num">Quota economica</th>
          <th className="md-num">Saldo socio/società</th>
          <th className="md-num">Già liquidato</th>
          <th className="md-num">Da liquidare</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.partnerId}>
            <td>{r.name}</td>
            <td className="md-num">{pct(r.quota)}</td>
            <td className="md-num">{eur(r.economicShare)}</td>
            <td className="md-num">{eur(r.accountPosition)}</td>
            <td className="md-num">{eur(r.alreadyPaid)}</td>
            <td className="md-num">{eur(r.toPay)}</td>
          </tr>
        ))}
        {rows.length === 0 && <DataTableEmptyRow colSpan={6} title="Nessun socio." />}
      </tbody>
    </DataTable>
  )
}

export function PartnerPositionsPanel() {
  const pickerOn = useFeatureFlag('utenti.selettore')
  // null = ancora in caricamento; 'none' = né Admin né socio collegato: il pannello non compare.
  const [admin, setAdmin] = useState<PartnerPositions | 'none' | null>(null)
  const [mine, setMine] = useState<{ label: string; available: boolean; reason: string | null; row: PartnerPositionRow | null; distributable: number | null } | null>(null)
  const [links, setLinks] = useState<PartnerLink[]>([])
  const [userId, setUserId] = useState('')
  const [partnerId, setPartnerId] = useState('')
  const [error, setError] = useState('')

  const loadLinks = useCallback(() => {
    partnerPositionsApi.links().then(setLinks).catch(() => setLinks([]))
  }, [])

  useEffect(() => {
    let cancelled = false
    partnerPositionsApi.list()
      .then(d => { if (!cancelled) { setAdmin(d); loadLinks() } })
      .catch(() => {
        // Non Admin: si prova la riga del socio collegato (404 = non collegato o lettura spenta).
        partnerPositionsApi.me()
          .then(m => { if (!cancelled) { setAdmin('none'); setMine({ label: m.label, available: m.available, reason: m.reason, row: m.position, distributable: m.distributableAvailable }) } })
          .catch(() => { if (!cancelled) setAdmin('none') })
      })
    return () => { cancelled = true }
  }, [loadLinks])

  async function addLink(e: FormEvent) {
    e.preventDefault()
    setError('')
    const id = Number(userId)
    if (!Number.isInteger(id) || id <= 0 || !partnerId) {
      setError("Indica l'utente e il socio da collegare.")
      return
    }
    try {
      await partnerPositionsApi.link(id, partnerId)
      setUserId('')
      loadLinks()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante il collegamento')
    }
  }

  async function removeLink(id: number) {
    setError('')
    try {
      await partnerPositionsApi.unlink(id)
      loadLinks()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante lo scollegamento')
    }
  }

  if (admin === null || (admin === 'none' && !mine)) return null

  if (admin === 'none' && mine) {
    return (
      <Card variant="outlined">
        <CardHeader title="La mia posizione (da conti)" />
        <CardContent>
          {!mine.available ? (
            <div className="text-muted">{REASON[mine.reason ?? ''] ?? 'Conti non è disponibile.'}</div>
          ) : (
            <div className="md-stack">
              {mine.row && <PositionTable rows={[mine.row]} />}
              <div className="md-typescale-body-small text-muted">Disponibilità distribuibile gestionale: {eur(mine.distributable)}</div>
              <Callout tone="warning">{mine.label}</Callout>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  if (admin === 'none' || admin === null) return null
  return (
    <Card variant="outlined">
      <CardHeader title="Posizioni dei soci (da conti)" />
      <CardContent>
        <div className="md-stack">
          {!admin.available ? (
            <Callout tone="warning">
              {REASON[admin.reason ?? ''] ?? admin.message ?? 'Conti non è disponibile.'}
            </Callout>
          ) : (
            <>
              <PositionTable rows={admin.rows} />
              <div className="md-typescale-body-small text-muted">Disponibilità distribuibile gestionale: {eur(admin.distributableAvailable)}</div>
              <Callout tone="warning">{admin.label}</Callout>
            </>
          )}

          <Divider />
          <section className="md-stack" aria-label="Collegamenti utente – socio">
            <h3 className="md-typescale-title-small m-0">Collegamenti utente – socio</h3>
            {error && <Callout tone="error">{error}</Callout>}
            {links.length === 0 ? (
              <div className="text-muted">Nessun collegamento.</div>
            ) : (
              <List aria-label="Collegamenti utente – socio">
                {links.map(l => (
                  <ListItem
                    key={l.userId}
                    headline={<>Utente {pickerOn ? <UserNameLabel id={l.userId} /> : l.userId} → socio {l.contiPartnerId}</>}
                    trailing={<IconButton icon="link_off" size="sm" aria-label="Scollega" onClick={() => void removeLink(l.userId)} />}
                  />
                ))}
              </List>
            )}
            <form className="md-cluster" onSubmit={addLink}>
              {pickerOn ? (
                <UserIdsPicker id="pp-user" label="Id utente" value={userId} onChange={setUserId} />
              ) : (
                <TextField id="pp-user" label="Id utente" type="number" min={1} value={userId} onChange={e => setUserId(e.target.value)} />
              )}
              <Select id="pp-partner" label="Socio" value={partnerId} onChange={e => setPartnerId(e.target.value)}>
                <option value="">Scegli…</option>
                {admin.rows.map(r => <option key={r.partnerId} value={r.partnerId}>{r.name}</option>)}
              </Select>
              <Button type="submit" variant="tonal">Collega</Button>
            </form>
          </section>
        </div>
      </CardContent>
    </Card>
  )
}
